import { useId, useRef, useState } from "react";
import { Upload, Trash2, ArrowUp, ArrowDown, Plus, Loader2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { adminUploadImage, errorMessage, imgUrl } from "@/lib/api";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/admin/PageHeader";

/**
 * Labelled field with hint + inline error. Wires label/hint/error to the control via aria-* when `id` is given
 * (or pass a render-function child to receive the generated id + aria props).
 */
export const Field = ({ label, hint, error, children, className, required, id, testId }) => {
  const auto = useId();
  const fieldId = id || auto;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errId = error ? `${fieldId}-error` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(" ") || undefined;
  const control = typeof children === "function" ? children({ id: fieldId, "aria-describedby": describedBy, "aria-invalid": error ? true : undefined }) : children;
  return (
    <div className={cn("space-y-1", className)} data-testid={testId}>
      {label && (
        <Label htmlFor={typeof children === "function" ? fieldId : undefined} className="flex items-baseline justify-between gap-2 text-[12.5px] font-medium text-foreground/85">
          <span>{label}</span>
          {required && <span className="text-[10.5px] font-normal text-muted-foreground">Påkravd</span>}
        </Label>
      )}
      {control}
      {error ? <p id={errId} role="alert" className="text-[12px] text-destructive">{error}</p> : hint ? <p id={hintId} className="text-[11.5px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
};

export const useUpload = () => {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return null;
    setUploading(true);
    try {
      const res = await adminUploadImage(file);
      toast.success("Biletet er lasta opp");
      return res.url;
    } catch (e) {
      toast.error(errorMessage(e, "Opplasting feila"));
      return null;
    } finally {
      setUploading(false);
    }
  };
  return { upload, uploading };
};

export const ImageField = ({ value, onChange, testId = "image-field" }) => {
  const ref = useRef(null);
  const { upload, uploading } = useUpload();
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-20 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-secondary">
        {value ? <img src={imgUrl(value, 320)} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
      </div>
      <div className="flex-1 space-y-2">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Lim inn bilet-URL eller last opp" className="h-9 bg-background" data-testid={`${testId}-url-input`} />
        <div className="flex flex-wrap gap-2">
          <input ref={ref} type="file" accept="image/*" className="hidden" onChange={async (e) => { const url = await upload(e.target.files?.[0]); if (url) onChange(url); e.target.value = ""; }} data-testid={`${testId}-file-input`} />
          <Button type="button" variant="outline" size="sm" className="h-9 bg-card" onClick={() => ref.current?.click()} disabled={uploading} data-testid={`${testId}-upload-button`}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Last opp
          </Button>
          {value && <Button type="button" variant="ghost" size="sm" className="h-9" onClick={() => onChange("")} data-testid={`${testId}-clear-button`}><Trash2 className="h-4 w-4" /> Fjern</Button>}
        </div>
      </div>
    </div>
  );
};

export const ImagesEditor = ({ images = [], onChange, testId = "images-editor" }) => {
  const ref = useRef(null);
  const [url, setUrl] = useState("");
  const { upload, uploading } = useUpload();
  const move = (i, dir) => {
    const next = [...images];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid={testId}>
      {images.length === 0 && <p className="rounded-lg border border-dashed border-input px-4 py-6 text-center text-sm text-muted-foreground">Ingen bilete enno.</p>}
      {images.length > 0 && (
        <ul className="grid grid-cols-[minmax(0,1fr)] gap-2 sm:grid-cols-2">
          {images.map((src, i) => (
            <li key={`${src}-${i}`} className="flex min-w-0 items-center gap-3 rounded-lg border border-border bg-background p-2" data-testid={`${testId}-item-${i}`}>
              <img src={imgUrl(src, 240)} alt="" className="h-14 w-20 shrink-0 rounded bg-secondary object-cover" />
              <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" title={src}>{i === 0 ? <span className="mr-1.5 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-foreground">Hovud</span> : null}{src}</span>
              <div className="flex shrink-0 gap-0.5">
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Flytt opp" data-testid={`${testId}-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={() => move(i, 1)} disabled={i === images.length - 1} aria-label="Flytt ned" data-testid={`${testId}-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive hover:text-destructive" onClick={() => onChange(images.filter((_, k) => k !== i))} aria-label="Fjern" data-testid={`${testId}-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Lim inn bilet-URL…" className="h-9 bg-background" aria-label="Bilet-URL" data-testid={`${testId}-url-input`} />
        <Button type="button" variant="outline" className="h-9 bg-card" disabled={!url.trim()} onClick={() => { onChange([...images, url.trim()]); setUrl(""); }} data-testid={`${testId}-add-url-button`}><Plus className="h-4 w-4" /> Legg til</Button>
        <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={async (e) => { const files = Array.from(e.target.files || []); const urls = []; for (const f of files) { const u = await upload(f); if (u) urls.push(u); } if (urls.length) onChange([...images, ...urls]); e.target.value = ""; }} data-testid={`${testId}-file-input`} />
        <Button type="button" className="h-9" onClick={() => ref.current?.click()} disabled={uploading} data-testid={`${testId}-upload-button`}>{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Last opp</Button>
      </div>
    </div>
  );
};

export const RoomsCheckboxes = ({ rooms = [], value = [], onChange, testId = "rooms-checkboxes" }) => {
  const WHOLE = "heile-huset";
  const toggle = (id) => {
    if (id === WHOLE) return onChange(value.includes(WHOLE) ? [] : [WHOLE]);
    const without = value.filter((x) => x !== WHOLE);
    onChange(without.includes(id) ? without.filter((x) => x !== id) : [...without, id]);
  };
  return (
    <div className="grid gap-2 sm:grid-cols-2" data-testid={testId} role="group" aria-label="Rom">
      {rooms.map((r) => {
        const on = value.includes(r.id);
        return (
          <label key={r.id} className={cn("admin-field flex min-h-[40px] cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-colors", on && "ring-1 ring-foreground/60")}>
            <Checkbox checked={on} onCheckedChange={() => toggle(r.id)} data-testid={`${testId}-${r.id}`} />
            <span className="min-w-0 flex-1 truncate">{r.name}</span>
            {r.id === WHOLE && <span className="t-mono-sm text-muted-foreground">Alt</span>}
          </label>
        );
      })}
    </div>
  );
};

export const ConfirmDialog = ({ open, onOpenChange, title, text, confirmLabel = "Stadfest", cancelLabel = "Avbryt", onConfirm, destructive = false, loading = false, testId = "confirm-dialog", children }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent className="bg-card" data-testid={testId}>
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display">{title}</AlertDialogTitle>
        {text && <AlertDialogDescription className="text-base leading-relaxed">{text}</AlertDialogDescription>}
      </AlertDialogHeader>
      {children}
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testId}-cancel`}>{cancelLabel}</AlertDialogCancel>
        <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(); }} disabled={loading} className={destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""} data-testid={`${testId}-confirm`}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

/**
 * One surface with an optional header row (no line under it – spacing does the work). `flat` drops the surface
 * entirely so the block sits straight on the canvas. Body gets horizontal padding; sections inside add their own rhythm.
 */
export const Panel = ({ title, description, action, children, className, bodyClassName, testId, flat = false }) => (
  <section className={cn(!flat && "overflow-hidden admin-card", className)} data-testid={testId}>
    {(title || action) && (
      <header className={cn("flex items-start justify-between gap-4", flat ? "mb-3" : "px-4 pb-2 pt-3.5 sm:px-5")}>
        <div className="min-w-0">
          {title && <h2 className="font-display text-[14px] font-semibold tracking-[-0.01em]">{title}</h2>}
          {description && <p className="mt-0.5 text-[12px] text-muted-foreground">{description}</p>}
        </div>
        {action && <div className="shrink-0 pt-px">{action}</div>}
      </header>
    )}
    <div className={cn(!flat && "px-4 sm:px-5", bodyClassName)}>{children}</div>
  </section>
);

/** Legacy wrapper – pages migrated to PageHeader directly, kept so nothing breaks. */
export const AdminPage = ({ title, intro, action, children }) => (
  <div>
    <PageHeader title={title} description={intro} actions={action} />
    <div className="space-y-6">{children}</div>
  </div>
);
